import axios from "axios";
import useRefreshToken from "./useRefreshToken";
import { handleAccountDeactivated } from "../utils/accountDeactivated";

const axiosPrivate = axios.create();

const useAxiosPrivate = async () => {
  const refresh = useRefreshToken();

  // Clear any existing interceptors to prevent duplicates
  axiosPrivate.interceptors.request.clear();
  axiosPrivate.interceptors.response.clear();

  axiosPrivate.interceptors.request.use(
    (config) => {
      const access_token = sessionStorage.getItem("usr_1a2b3c");
      const refresh_token = sessionStorage.getItem("usr_4d5e6f");

      if (!config.headers["Authorization"] && access_token) {
        config.headers["Authorization"] = `Bearer ${access_token}`;
      }
      if (!config.headers["User-Refresh-Token"] && refresh_token) {
        config.headers["User-Refresh-Token"] = `${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosPrivate.interceptors.response.use(
    (response) => {
      // Handle success response
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      if (handleAccountDeactivated(error)) {
        return Promise.reject(error);
      }

      // Only attempt refresh for 401 errors and if we haven't already tried
      if (error?.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          console.log(
            "🔄 Axios interceptor: 401 detected, attempting token refresh...",
          );

          // Call refresh token API
          const newTokens = await refresh();

          if (newTokens && newTokens.access_token) {
            // Save new tokens
            sessionStorage.setItem("usr_1a2b3c", newTokens.access_token);
            sessionStorage.setItem("usr_4d5e6f", newTokens.refresh_token);

            // Update the original request headers
            originalRequest.headers["Authorization"] =
              `Bearer ${newTokens.access_token}`;
            originalRequest.headers["User-Refresh-Token"] =
              `${newTokens.refresh_token}`;

            console.log(
              "✅ Axios interceptor: Token refreshed, retrying request",
            );

            // Retry the original request
            return axiosPrivate(originalRequest);
          } else {
            console.error(
              "❌ Axios interceptor: Token refresh failed - no tokens received",
            );
            throw new Error("Token refresh failed");
          }
        } catch (refreshError) {
          console.error(
            "❌ Axios interceptor: Token refresh failed:",
            refreshError,
          );

          // Check if it's a refresh token expiry
          if (
            refreshError?.response?.status === 401 ||
            refreshError?.response?.data?.error_code === "AUTH_010" ||
            refreshError?.response?.data?.message?.includes(
              "Invalid or expired refresh token",
            )
          ) {
            console.log(
              "🚨 Refresh token expired, clearing session and redirecting to login",
            );
            sessionStorage.clear();
            window.location.href = "/";
          }

          return Promise.reject(refreshError);
        }
      }

      // For non-401 errors or if retry already attempted, just reject
      return Promise.reject(error);
    },
  );

  return axiosPrivate;
};

export default useAxiosPrivate;
